import { RelativePathModel } from '@/react/portainer/gitops/types';
import { HelmInstallOptionsSection } from '@/react/edge/edge-stacks/CreateView/HelmChartForm/HelmInstallOptionsSection';
import {
  hasDynamicVariable,
  renderStyledPath,
} from '@/react/edge/edge-stacks/CreateView/HelmChartForm/helmDynamicVariables';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { TextTip } from '@@/Tip/TextTip';

interface HelmDeploymentFieldsetFormValues {
  helmChartPath?: string;
  helmVersion?: string;
  helmValuesFiles?: string[];
}

interface HelmDeploymentFieldsetProps {
  stackName: string;
  values: HelmDeploymentFieldsetFormValues;
  relativePath?: RelativePathModel;
}

function getDeviceMatchTypeLabel(matchType: 'file' | 'dir' | ''): string {
  if (matchType === 'file') {
    return 'Match file name with Portainer Edge ID';
  }
  if (matchType === 'dir') {
    return 'Match folder name with Portainer Edge ID';
  }
  return '(Not configured)';
}

function getGroupMatchTypeLabel(matchType: 'file' | 'dir' | ''): string {
  if (matchType === 'file') {
    return 'Match file name with Edge Group';
  }
  if (matchType === 'dir') {
    return 'Match folder name with Edge Group';
  }
  return '(Not configured)';
}

export function HelmDeploymentFieldset({
  stackName,
  values,
  relativePath,
}: HelmDeploymentFieldsetProps) {
  const { helmChartPath } = values;
  const helmValuesFiles = values.helmValuesFiles || [];
  return (
    <>
      <FormSection title="Deploy to">
        <FormControl label="Release name" inputId="helm-release-name">
          <Input
            id="helm-release-name"
            value={`edge-${stackName}`}
            readOnly
            disabled
            data-cy="helm-release-name-input"
          />
        </FormControl>
      </FormSection>

      <FormSection title="Helm chart">
        <TextTip color="blue" className="mb-3">
          Helm chart configuration from your Git repository. The version is
          determined by Chart.yaml.
        </TextTip>

        <FormControl
          label="Helm chart path"
          inputId="helm-chart-path"
          tooltip={
            helmChartPath
              ? 'Path to the Helm chart folder in the Git repository'
              : 'Using the root folder of the repository'
          }
        >
          <Input
            id="helm-chart-path"
            value={helmChartPath || './'}
            readOnly
            disabled
            data-cy="helm-chart-path-input"
          />
        </FormControl>
      </FormSection>

      {relativePath?.SupportPerDeviceConfigs && (
        <FormSection title="GitOps Edge configurations">
          <TextTip color="blue" className="mb-3">
            GitOps Edge configurations use dynamic variables to deploy
            device-specific or group-specific Helm values files. This
            configuration is set during stack creation and cannot be modified.
          </TextTip>

          <FormControl
            label="Configuration path"
            inputId="helm-per-device-config-path"
            tooltip="Directory in the Git repository containing per-device/group configuration files"
          >
            <Input
              id="helm-per-device-config-path"
              value={relativePath.PerDeviceConfigsPath || '(Not configured)'}
              readOnly
              disabled
              data-cy="helm-per-device-config-path-input"
            />
          </FormControl>

          {relativePath.PerDeviceConfigsMatchType && (
            <FormControl
              label="Device matching rule"
              inputId="helm-per-device-match-type"
              tooltip="How device-specific configurations are matched"
            >
              <Input
                id="helm-per-device-match-type"
                value={getDeviceMatchTypeLabel(
                  relativePath.PerDeviceConfigsMatchType
                )}
                readOnly
                disabled
                data-cy="helm-per-device-match-type-input"
              />
            </FormControl>
          )}

          {relativePath.PerDeviceConfigsGroupMatchType && (
            <FormControl
              label="Group matching rule"
              inputId="helm-per-device-group-match-type"
              tooltip="How group-specific configurations are matched"
            >
              <Input
                id="helm-per-device-group-match-type"
                value={getGroupMatchTypeLabel(
                  relativePath.PerDeviceConfigsGroupMatchType
                )}
                readOnly
                disabled
                data-cy="helm-per-device-group-match-type-input"
              />
            </FormControl>
          )}
        </FormSection>
      )}

      <FormSection title="Install options">
        <FormControl
          label="Values files"
          tooltip="User-defined values files to override the default chart values. Paths are relative to the Helm chart folder in your Git repository."
        >
          {helmValuesFiles.length > 0 ? (
            <div className="space-y-3">
              {helmValuesFiles.map((file, index) => (
                <div key={`values-file-${index}`}>
                  {hasDynamicVariable(file) ? (
                    <div>
                      <span className="badge badge-info text-xs">
                        Dynamic:{' '}
                        {file.includes('EDGE_GROUP') ? 'Group' : 'Device'}
                      </span>
                      {renderStyledPath(file)}
                    </div>
                  ) : (
                    <div className="text-sm font-mono">{file}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <TextTip color="orange">No values files configured</TextTip>
          )}
        </FormControl>
      </FormSection>

      <HelmInstallOptionsSection />
    </>
  );
}
